import { useEffect } from 'react';
import type { Dayjs } from 'dayjs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DATE_TIME_FORMAT } from '@/lib/dayjs';
import { useAuthorLabel, useUserDirectory } from '@/stores/useUserDirectory';

/**
 * Date de création ou de modification (02/10/2026 18h10) ; son auteur apparaît au survol ou au focus clavier :
 * « Par DOE Jane » (createdBy / updatedBy : URN de la personne connectée, ou son email pour les données anciennes ;
 * nom demandé à l'API par useUserDirectory), « Par le système » sans auteur (fixtures, migration, import, consumer).
 */
export function AuditDate({ date, by }: { date?: Dayjs; by?: string | null }) {
  const author = useAuthor(by);

  if (!date) return <>—</>;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="underline decoration-dotted decoration-slate-400 underline-offset-4 cursor-help">
          {date.format(DATE_TIME_FORMAT)}
        </span>
      </TooltipTrigger>
      <TooltipContent>Par {author}</TooltipContent>
    </Tooltip>
  );
}

/** Auteur seul (ex. colonne « Par » d'un historique, signataire) : « DOE Jane », ou « Le système » sans auteur */
export function AuthorName({ by }: { by?: string | null }) {
  const author = useAuthor(by);
  return <>{by ? author : 'Le système'}</>;
}

/** Libellé de l'auteur, son nom étant demandé à l'API au besoin (requêtes groupées, une fois par auteur) */
function useAuthor(by: string | null | undefined): string {
  const requestNames = useUserDirectory((state) => state.request);

  useEffect(() => {
    if (by) void requestNames([by]);
  }, [by, requestNames]);

  return useAuthorLabel(by);
}
